/**
 * 患者情報検索 / Search Patient List API service.
 * Legacy RA2: /patients/search_patients.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import type {
  PrintSearchPatientPayload,
  SearchPatientListParams,
  SearchPatientListResponse,
  SearchPatientListMasterDataResponse,
} from '@/pages/main/searchPatientList/types';

function toParams(filters: SearchPatientListParams): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value) && value.length === 0) return;
    out[key] = value;
  });
  return out;
}

export async function searchPatientList(
  siteType: string,
  clinicNumber: string,
  filters: SearchPatientListParams
): Promise<SearchPatientListResponse> {
  const response = await apiClient.get<SearchPatientListResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/search-patients'),
    {
      params: toParams(filters),
      // indexes:false → a[]=1&a[]=2 (Laravel array). indexes:null would drop brackets entirely.
      paramsSerializer: { indexes: false },
    }
  );
  return response.data;
}

export async function getSearchPatientListMasterData(
  siteType: string,
  clinicNumber: string
): Promise<SearchPatientListMasterDataResponse> {
  const response = await apiClient.get<SearchPatientListMasterDataResponse>(
    clinicApiPath(siteType, clinicNumber, '/patients/search-patients/master-data')
  );
  return response.data;
}

/**
 * All patient IDs matching the filter (no pagination).
 * Backs the table's 全選択 cross-page select-all behavior.
 */
export async function getSearchPatientListAllIds(
  siteType: string,
  clinicNumber: string,
  filters: SearchPatientListParams
): Promise<number[]> {
  const response = await apiClient.get<{ success: boolean; data: { patient_ids: number[] } }>(
    clinicApiPath(siteType, clinicNumber, '/patients/search-patients/all-ids'),
    {
      params: toParams(filters),
      paramsSerializer: { indexes: false },
    }
  );
  return response.data?.data?.patient_ids ?? [];
}

/**
 * PDF print endpoints — 3 identical shapes differing only in URL suffix.
 * All return a binary PDF blob (`Content-Type: application/pdf`).
 */
async function postPrintBlob(
  siteType: string,
  clinicNumber: string,
  subPath: string,
  payload: PrintSearchPatientPayload
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, subPath),
    payload,
    { responseType: 'blob' }
  );
  return response.data as Blob;
}

export function printSearchPatientListPdf(
  siteType: string,
  clinicNumber: string,
  payload: PrintSearchPatientPayload
): Promise<Blob> {
  return postPrintBlob(siteType, clinicNumber, '/patients/search-patients/print-list', payload);
}

export function printSearchPatientPostcardPdf(
  siteType: string,
  clinicNumber: string,
  payload: PrintSearchPatientPayload
): Promise<Blob> {
  return postPrintBlob(siteType, clinicNumber, '/patients/search-patients/print-postcard', payload);
}

export function printSearchPatientLabelPdf(
  siteType: string,
  clinicNumber: string,
  payload: PrintSearchPatientPayload
): Promise<Blob> {
  return postPrintBlob(siteType, clinicNumber, '/patients/search-patients/print-label', payload);
}

/**
 * CSV出力 — 12-column UTF-8 BOM CSV. Returns blob (text/csv; charset=UTF-8).
 * Payload shape matches print endpoints; kept as separate fn for grep-ability + future divergence.
 */
export async function exportSearchPatientCsv(
  siteType: string,
  clinicNumber: string,
  payload: PrintSearchPatientPayload
): Promise<Blob> {
  const response = await apiClient.post(
    clinicApiPath(siteType, clinicNumber, '/patients/search-patients/export-csv'),
    payload,
    { responseType: 'blob' }
  );
  return response.data as Blob;
}
